/**
 * BelowFoldContext - Tracks when below-the-fold content becomes visible
 *
 * This context allows components to defer expensive operations (like API calls)
 * until the user scrolls to content that actually needs that data.
 */
import { createContext, useContext, useState, ReactNode, useCallback } from 'react'

interface BelowFoldContextValue {
  isBelowFoldVisible: boolean
  markBelowFoldVisible: () => void
}

const BelowFoldContext = createContext<BelowFoldContextValue | undefined>(undefined)

export const useBelowFold = (): BelowFoldContextValue => {
  const context = useContext(BelowFoldContext)
  if (context === undefined) {
    // Default to visible if context is not provided (for backwards compatibility)
    return { isBelowFoldVisible: true, markBelowFoldVisible: () => {} }
  }
  return context
}

interface BelowFoldProviderProps {
  children: ReactNode
}

export const BelowFoldProvider = ({ children }: BelowFoldProviderProps) => {
  const [isBelowFoldVisible, setIsBelowFoldVisible] = useState(false)

  const markBelowFoldVisible = useCallback(() => {
    setIsBelowFoldVisible(true)
  }, [])

  return (
    <BelowFoldContext.Provider value={{ isBelowFoldVisible, markBelowFoldVisible }}>
      {children}
    </BelowFoldContext.Provider>
  )
}
